import type { Ref } from 'react'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'

interface LockedNavTargetProps {
  /** The class the live version of this target wears, plus its locked styling. */
  className: string
  /** Why it is locked — the only explanation attached to the target. */
  reason: string | undefined
  testId: string
  /** Only where the target has no visible text of its own (the rail's icons). */
  label?: string
  /**
   * The product tour's anchor registration, when this destination is one of its
   * stops. Forwarded because the locked rendering is the state the tour runs in:
   * step 4 spotlights nav targets it has itself disabled.
   */
  ref?: Ref<HTMLSpanElement>
  children: React.ReactNode
}

/**
 * A nav destination that stays visible but is not available yet — currently
 * only the founder onboarding lock (ClickUp 86d3uknt8).
 *
 * `aria-disabled` on a focusable span rather than a `disabled` control: the
 * tooltip is the only explanation attached to a locked target, and `disabled`
 * would both swallow the pointer events that trigger it and drop the item out
 * of the tab order — leaving a keyboard user a dead target with no reason on it.
 *
 * One component rather than the shape repeated per call site, so that contract
 * is decided and documented once. The rail, the avatar and the section sidebar
 * all render this; only the styling and the test id differ.
 */
export function LockedNavTarget({ className, reason, testId, label, ref, children }: LockedNavTargetProps) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          ref={ref}
          className={className}
          role="link"
          tabIndex={0}
          aria-label={label}
          aria-disabled="true"
          data-locked="true"
          data-testid={testId}
        >
          {children}
        </span>
      </TooltipTrigger>
      <TooltipContent side="right">{reason}</TooltipContent>
    </Tooltip>
  )
}
